import { decodeHex, encodeHex } from './hexService'

const KEY_LENGTH = 256
const IV_SIZE = 16
const AES_GCM = 'AES-GCM'

/**
 * AesService provides methods to load a secret key,
 * encrypt plain text and decrypt cipher text using AES-GCM.
 */
export class AesService {
  /**
   * Imports a raw key from a hex-encoded secret key.
   * @param hexSecretKey - The hex encoded secret key.
   * @returns A Promise that resolves to a CryptoKey.
   */
  async loadSecretKey(hexSecretKey: string): Promise<CryptoKey> {
    const keyData = decodeHex(hexSecretKey)
    return crypto.subtle.importKey('raw', keyData, { name: AES_GCM }, false, [
      'encrypt',
      'decrypt',
    ])
  }

  /**
   * Encrypts the given plain text using AES-GCM.
   * The returned string contains the IV prepended (both encoded in hex).
   * @param plainText - The plain text to encrypt.
   * @param secretKey - The CryptoKey to use for encryption.
   * @returns A Promise that resolves to the hex encoded cipher text.
   */
  async encrypt(plainText: string, secretKey: CryptoKey): Promise<string> {
    // Generate a random initialization vector (IV)
    const iv = crypto.getRandomValues(new Uint8Array(IV_SIZE))
    const encodedText = new TextEncoder().encode(plainText)
    const cipherBuffer = await crypto.subtle.encrypt(
      {
        name: AES_GCM,
        iv,
        length: KEY_LENGTH,
        tagLength: KEY_LENGTH / 2,
      },
      secretKey,
      encodedText,
    )
    const cipherArray = new Uint8Array(cipherBuffer)
    // Prepend the IV (hex-encoded) to the cipher text
    return encodeHex(iv) + encodeHex(cipherArray)
  }

  /**
   * Decrypts an AES-GCM encrypted hex string.
   * @param ivEncryptedContent - The hex string with the IV prepended.
   * @param aesKey - The CryptoKey to use for decryption.
   * @returns A Promise that resolves to the decrypted plain text.
   */
  async decrypt(
    ivEncryptedContent: string,
    aesKey: CryptoKey,
  ): Promise<string> {
    // Extract IV (first IV_SIZE * 2 hex characters) and cipher text.
    const ivHex = ivEncryptedContent.slice(0, IV_SIZE * 2)
    const bodyHex = ivEncryptedContent.slice(IV_SIZE * 2)
    const iv = decodeHex(ivHex)
    const cipherText = decodeHex(bodyHex)
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: AES_GCM,
        iv,
        length: KEY_LENGTH,
        tagLength: KEY_LENGTH / 2,
      },
      aesKey,
      cipherText,
    )
    return new TextDecoder().decode(decryptedBuffer)
  }
}

export const aesService = new AesService()
